import { EmailDocumentSchema, type EmailDocument } from './emails.js';

// ─────────────────────────────────────────────────────────────────
// Moving an email document in and out of the product
//
// The export, the import dialog and the create endpoint all speak
// `EmailDocumentSchema` (see the note there). These are the pure halves: turn a
// stored row into a document, and turn any pasted JSON back into one.
//
// They live here rather than in a component because both the editor and the API
// read the same shape — a second reader would be a second set of rules about
// what an imported file may contain, which is exactly how the export and the
// import drifted apart in the first place.
// ─────────────────────────────────────────────────────────────────

/**
 * A stored JSON column, read leniently: a row written before the column was
 * typed may still hold its JSON as a string, and a half-written one must not
 * take the whole export down with it.
 */
function jsonValue(value: unknown, fallback: unknown): unknown {
  if (typeof value !== 'string') return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

/**
 * The document a stored email row holds.
 *
 * Parses the columns kept as JSON text (`blocks`, `designOverrides`,
 * `componentUsages`), carries every authored field, and drops the server-managed
 * ones — see `EmailDocumentSchema` for that boundary. It throws only when a row
 * cannot describe a valid document, which is worth failing an export over
 * rather than writing a file that is quietly incomplete.
 */
export function emailDocumentFromRow(row: Record<string, unknown>): EmailDocument {
  return EmailDocumentSchema.parse({
    subject: row['subject'],
    previewText: row['previewText'] ?? undefined,
    blocks: jsonValue(row['blocks'], []),
    designOverrides: jsonValue(row['designOverrides'], undefined),
    variableDefinition: jsonValue(row['variableDefinition'], undefined),
    footerOverride: jsonValue(row['footerOverride'], undefined),
    calendarInvite: jsonValue(row['calendarInvite'], undefined),
    attachments: jsonValue(row['attachments'], []),
    componentUsages: jsonValue(row['componentUsages'], []),
    defaultFont: row['defaultFont'] ?? undefined,
    contentPadding: row['contentPadding'] ?? undefined,
  });
}

export type EmailDocumentParseResult =
  | { ok: true; document: EmailDocument }
  | { ok: false; error: string };

/**
 * Read pasted or uploaded JSON as a document.
 *
 * A bare blocks array is still accepted — it is the shortest thing an author or
 * an assistant produces — and becomes a document with a placeholder subject, as
 * it always has. The errors say what is wrong with the *input* rather than
 * quoting a Zod issue, because this is text a person pasted.
 */
export function parseEmailDocument(raw: unknown): EmailDocumentParseResult {
  let source: Record<string, unknown>;

  if (Array.isArray(raw)) {
    source = { blocks: raw };
  } else if (raw && typeof raw === 'object') {
    source = raw as Record<string, unknown>;
  } else {
    return { ok: false, error: 'Invalid format: expected a blocks array or an email object' };
  }

  if (!Array.isArray(source['blocks'])) {
    return {
      ok: false,
      error: 'Invalid format: expected a blocks array or an object with a "blocks" array property',
    };
  }
  if (!everyBlockLooksLikeABlock(source['blocks'])) {
    return { ok: false, error: 'Invalid format: each block must have an "id" and "type" property' };
  }

  const subject =
    typeof source['subject'] === 'string' && source['subject'].trim()
      ? source['subject']
      : 'Imported email';
  const parsed = EmailDocumentSchema.safeParse({ ...source, subject });

  if (!parsed.success) {
    // A variables record that does not fit its own schema is the mistake worth
    // naming: it is the one an author or an assistant edits by hand.
    const badVariables = parsed.error.issues.some((issue) => issue.path[0] === 'variableDefinition');
    return {
      ok: false,
      error: badVariables ? 'Invalid variable definition' : formatIssues(parsed.error.issues),
    };
  }

  return { ok: true, document: parsed.data };
}

/** One block per entry, each carrying the two fields every block type has. */
function everyBlockLooksLikeABlock(blocks: unknown[]): boolean {
  return blocks.every(
    (block) =>
      block !== null && typeof block === 'object' && 'id' in block && 'type' in block,
  );
}

/** The first schema complaint, as a sentence the dialog can show. */
function formatIssues(issues: { path: (string | number)[]; message: string }[]): string {
  const [first] = issues;
  if (!first) return 'Invalid email document';
  const where = first.path.length > 0 ? `${first.path.join('.')}: ` : '';
  return `Invalid email document — ${where}${first.message}`;
}
